import React from 'react';
import { Smartphone, Sparkles, SquarePlus } from 'lucide-react';
import { motion } from 'framer-motion';

interface AppLibraryEmptyStateProps {
  isLoading: boolean;
  hasError: boolean;
  hasData: boolean;
  selectedCategory: string;
  onRequestApp?: (category?: string) => void;
}

export const AppLibraryEmptyState: React.FC<AppLibraryEmptyStateProps> = ({
  isLoading,
  hasError,
  hasData,
  selectedCategory,
  onRequestApp,
}) => {
  if (isLoading || hasError || hasData) {
    return null;
  }

  const handleRequestApp = () => {
    if (onRequestApp) {
      const categoryToPass = selectedCategory !== 'all' && selectedCategory !== 'favorites' ? selectedCategory : undefined;
      onRequestApp(categoryToPass);
    } else {
      console.log('Request app functionality to be implemented');
    }
  };

  const getEmptyStateText = () => {
    switch (selectedCategory) {
      case 'favorites':
        return {
          title: 'No favorite apps yet',
          subtitle: 'Start exploring apps and mark your favorites to see them here.',
        };
      case 'productivity':
        return {
          title: 'No productivity apps found',
          subtitle: 'Discover powerful tools to boost your productivity and streamline your workflow.',
        };
      case 'creative':
        return {
          title: 'No creative apps found',
          subtitle: 'Unleash your creativity with apps designed for artists, designers, and creators.',
        };
      case 'education':
        return {
          title: 'No education apps found',
          subtitle: 'Expand your knowledge with learning tools and educational resources.',
        };
      case 'finance':
        return {
          title: 'No finance apps found',
          subtitle: 'Manage your money better with budgeting, investment, and financial tracking tools.',
        };
      case 'health':
        return {
          title: 'No health apps found',
          subtitle: 'Take care of your wellbeing with fitness tracking, nutrition, and wellness apps.',
        };
      case 'entertainment':
        return {
          title: 'No entertainment apps found',
          subtitle: 'Discover fun and engaging apps for gaming, media, and leisure activities.',
        };
      case 'shopping':
        return {
          title: 'No shopping apps found',
          subtitle: 'Find the best deals and manage your purchases with e-commerce and retail tools.',
        };
      default:
        return {
          title: 'Want More Apps?',
          subtitle: 'Get all your favorite apps built into one platform.',
        };
    }
  };

  const { title, subtitle } = getEmptyStateText();

  return (
    <motion.div 
      className="text-center py-16 px-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Animated icon container */}
      <motion.div 
        className="relative mx-auto mb-6 w-20 h-20"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.2 }}
      >
        {/* Background glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full blur-xl"></div>
        
        {/* Main icon background */}
        <div className="relative w-full h-full bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
          <Smartphone className="h-10 w-10 text-white" />
          
          {/* Floating sparkles */}
          <motion.div
            className="absolute -top-2 -right-2"
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Sparkles className="h-4 w-4 text-yellow-400" />
          </motion.div>
        </div>
      </motion.div>

      {/* Main heading */}
      <motion.h3 
        className="text-2xl font-bold mb-3 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {title}
      </motion.h3>
      
      {/* Subtitle */}
      <motion.p 
        className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto leading-relaxed"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        {subtitle}
      </motion.p>

      {/* Call-to-action button */}
      <motion.button
        onClick={handleRequestApp}
        className="group relative overflow-hidden bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Button shine effect */}
        <span className="absolute inset-0 w-0 h-full bg-white opacity-20 transition-all duration-500 group-hover:w-full"></span>
        
        {/* Button content */}
        <span className="relative flex items-center gap-2">
          <SquarePlus className="h-5 w-5" />
          Request an App
        </span>
      </motion.button>


    </motion.div>
  );
};

export default AppLibraryEmptyState; 